import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch } from '../client';

// ── Types ───────────────────────────────────────────────────────────────────

interface IssueSummary {
  id: string;
  status: string;
  assignee: string;
  summary: string;
  priority: string;
  severity: string;
  issueStage: string;
  affectedArea: string;
  affectedRoute: string;
  reportedBy: string;
  createdAt: string;
  updatedAt: string;
}

interface Screenshot {
  id: number;
  ticket_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  screenshot_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

interface FixPlan {
  id: number;
  ticket_id: string;
  plan_text: string;
  created_by: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface IssueDetail {
  id: string;
  status: string;
  priority: string;
  assignee: string;
  requestedBy: string;
  channel: string;
  summary: string;
  plan: Record<string, unknown>;
  history: { ts: string; type: string; agent: string; note: string }[];
  data: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  screenshots: Screenshot[];
  fixPlans: FixPlan[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface IssueFilters {
  status?: string;
  severity?: string;
  assignee?: string;
  limit?: number;
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useIssues(filters: IssueFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.assignee) params.set('assignee', filters.assignee);
  if (filters.limit) params.set('limit', String(filters.limit));

  const query = params.toString();
  const path = `/issues${query ? `?${query}` : ''}`;

  return useQuery({
    queryKey: ['issues', filters],
    queryFn: () => api<ApiResponse<IssueSummary[]>>(path).then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useIssueDetail(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['issues', ticketId],
    queryFn: () => api<ApiResponse<IssueDetail>>(`/issues/${ticketId}`).then((r) => r.data),
    enabled: !!ticketId,
    staleTime: 15_000,
  });
}

export function useCreateIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await apiFetch('/issues', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      return res.json() as Promise<ApiResponse<IssueDetail>>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
    },
  });
}

export function useTransitionIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, stage }: { ticketId: string; stage: string }) => {
      return api<ApiResponse<IssueDetail>>(`/issues/${ticketId}/transition`, {
        method: 'POST',
        body: JSON.stringify({ stage }),
      });
    },
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['issues', ticketId] });
    },
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, text }: { ticketId: string; text: string }) => {
      return api<ApiResponse<unknown>>(`/issues/${ticketId}/comment`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
    },
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['issues', ticketId] });
    },
  });
}

export function useCreateFixPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, planText }: { ticketId: string; planText: string }) => {
      return api<ApiResponse<FixPlan>>(`/issues/${ticketId}/fix-plan`, {
        method: 'POST',
        body: JSON.stringify({ planText }),
      });
    },
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['issues', ticketId] });
    },
  });
}

export function useUpdateFixPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, planId, planText, status }: {
      ticketId: string;
      planId: number;
      planText?: string;
      status?: string;
    }) => {
      return api<ApiResponse<FixPlan>>(`/issues/${ticketId}/fix-plan/${planId}`, {
        method: 'PATCH',
        body: JSON.stringify({ planText, status }),
      });
    },
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['issues', ticketId] });
    },
  });
}

export function useUploadScreenshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, file, type }: { ticketId: string; file: File; type: string }) => {
      const formData = new FormData();
      formData.append('screenshot', file);
      formData.append('type', type);

      const res = await apiFetch(`/issues/${ticketId}/screenshots`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      return res.json() as Promise<ApiResponse<Screenshot>>;
    },
    onSuccess: (_data, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['issues', ticketId] });
    },
  });
}
